import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Clock, 
  Plus, 
  ArrowRightLeft,
  CheckCircle,
  XCircle,
  Loader2,
  Calendar,
  User,
  Download,
  Lock,
  Globe,
  Mail,
  Phone,
  MessageCircle,
  MapPin
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CONTACT_METHODS = [
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'phone', label: 'Phone/SMS', icon: Phone },
  { value: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
  { value: 'signal', label: 'Signal', icon: MessageCircle },
  { value: 'in_person', label: 'In Person', icon: MapPin }
];

export function ExchangesPage() {
  const { user, refreshUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [exchanges, setExchanges] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewExchange, setShowNewExchange] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [exchangeForm, setExchangeForm] = useState({
    partner_user_id: '',
    description: '',
    hours: 1,
    exchange_type: 'gave',
    contact_method: 'email',
    contact_notes: '',
    is_private: true,
    recurrence: 'none',
    recurrence_end_date: ''
  });

  // Pre-fill Log Exchange from Community Board deep link (?log=1&partner_id=…)
  useEffect(() => {
    if (searchParams.get('log') !== '1') return;
    const partnerId = searchParams.get('partner_id') || '';
    const hoursRaw = searchParams.get('hours');
    const parsedHours = hoursRaw ? parseFloat(hoursRaw) : 1;
    setExchangeForm(prev => ({
      ...prev,
      partner_user_id: partnerId,
      hours: Number.isFinite(parsedHours) && parsedHours > 0 ? parsedHours : 1,
      description: searchParams.get('description') || prev.description,
      contact_method: searchParams.get('contact_method') || prev.contact_method,
      exchange_type: (searchParams.get('type') === 'received' ? 'received' : 'gave'),
    }));
    setShowNewExchange(true);
    // strip params so refresh doesn't re-open
    const cleaned = new URLSearchParams(searchParams);
    ['log','partner_id','hours','description','contact_method','type'].forEach(k => cleaned.delete(k));
    setSearchParams(cleaned, { replace: true });
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [exchangesRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/api/exchanges`, { withCredentials: true }),
        axios.get(`${API_URL}/api/users`, { withCredentials: true })
      ]);
      setExchanges(exchangesRes.data);
      setUsers(usersRes.data.filter(u => u.user_id !== user?.user_id));
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.error('Error fetching data:', err);
      toast.error('Failed to load exchanges');
    } finally {
      setLoading(false);
    }
  }, [user?.user_id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateExchange = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/exchanges`, exchangeForm, { withCredentials: true });
      setExchanges([response.data, ...exchanges]);
      setShowNewExchange(false);
      setExchangeForm({ 
        partner_user_id: '', 
        description: '', 
        hours: 1, 
        exchange_type: 'gave',
        contact_method: 'email',
        contact_notes: '',
        is_private: true,
        recurrence: 'none',
        recurrence_end_date: ''
      });
      const recCount = exchangeForm.recurrence !== 'none' && exchangeForm.recurrence_end_date
        ? ' (plus future occurrences scheduled)' : '';
      toast.success(`Exchange logged${recCount}! Your partner will receive an email notification.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create exchange');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmExchange = async (exchangeId) => {
    try {
      await axios.put(`${API_URL}/api/exchanges/${exchangeId}/confirm`, {}, { withCredentials: true });
      setExchanges(exchanges.map(e => e.exchange_id === exchangeId ? {...e, status: 'confirmed'} : e));
      toast.success('Exchange confirmed!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to confirm exchange');
    }
  };

  const handleCompleteExchange = async (exchangeId) => {
    try {
      await axios.put(`${API_URL}/api/exchanges/${exchangeId}/complete`, {}, { withCredentials: true });
      setExchanges(exchanges.map(e => e.exchange_id === exchangeId ? {...e, status: 'completed'} : e));
      refreshUser();
      toast.success('Exchange completed! Credits have been updated.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to complete exchange');
    }
  };

  const handleCancelExchange = async (exchangeId) => {
    try {
      await axios.put(`${API_URL}/api/exchanges/${exchangeId}/cancel`, {}, { withCredentials: true });
      setExchanges(exchanges.map(e => e.exchange_id === exchangeId ? {...e, status: 'cancelled'} : e));
      toast.success('Exchange cancelled');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel exchange');
    }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API_URL}/api/exchanges/export`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `timebank_exchanges_${user?.user_id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Exchange history exported!');
    } catch (err) {
      toast.error('Failed to export exchange history');
    } finally {
      setExporting(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: 'bg-[#ffd703]/10', text: 'text-[#ffd703]' },
      confirmed: { bg: 'bg-[#d1c7e9]/10', text: 'text-[#d1c7e9]' },
      completed: { bg: 'bg-[#22c55e]/10', text: 'text-[#22c55e]' },
      cancelled: { bg: 'bg-[#ef4444]/10', text: 'text-[#ef4444]' }
    };
    const style = styles[status] || styles.pending;
    return <Badge className={`${style.bg} ${style.text}`}>{status}</Badge>;
  };

  const getContactMethodIcon = (method) => {
    const found = CONTACT_METHODS.find(m => m.value === method);
    return found ? found.icon : Mail;
  };

  const getContactMethodLabel = (method) => {
    const found = CONTACT_METHODS.find(m => m.value === method);
    return found ? found.label : method;
  };

  const pendingExchanges = exchanges.filter(e => e.status === 'pending');
  const confirmedExchanges = exchanges.filter(e => e.status === 'confirmed');
  const completedExchanges = exchanges.filter(e => e.status === 'completed');

  return (
    <div className="min-h-screen">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="exchanges-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-[#090000] mb-2">
              Time Exchanges
            </h1>
            <p className="text-[#090000/60]">
              Log, track, and manage your time exchanges with community members
            </p>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              className=""
              onClick={handleExportPDF}
              disabled={exporting}
              data-testid="export-pdf-btn"
            >
              {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
              Export PDF
            </Button>
            <Dialog open={showNewExchange} onOpenChange={setShowNewExchange}>
              <DialogTrigger asChild>
                <Button className="bg-[#ffd703] hover:bg-[#e6c203]" data-testid="log-exchange-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Log Exchange
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>Log a Time Exchange</DialogTitle>
                  <DialogDescription>Record an exchange of time. Optionally schedule recurring sessions.</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateExchange} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Exchange Partner</Label>
                    <Select 
                      value={exchangeForm.partner_user_id} 
                      onValueChange={(v) => setExchangeForm({...exchangeForm, partner_user_id: v})}
                    >
                      <SelectTrigger data-testid="exchange-partner">
                        <SelectValue placeholder="Select a member" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.map(u => (
                          <SelectItem key={u.user_id} value={u.user_id}>
                            <span className="flex items-center gap-2">
                              <User className="w-4 h-4" />
                              {u.name} ({u.email})
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      placeholder="What was exchanged? e.g., Python tutoring session"
                      value={exchangeForm.description}
                      onChange={(e) => setExchangeForm({...exchangeForm, description: e.target.value})}
                      required
                      rows={3}
                      data-testid="exchange-description"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Hours</Label>
                      <Input
                        type="number"
                        min="0.5"
                        step="0.5"
                        value={exchangeForm.hours}
                        onChange={(e) => setExchangeForm({...exchangeForm, hours: parseFloat(e.target.value)})}
                        required
                        data-testid="exchange-hours"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Type</Label>
                      <Select 
                        value={exchangeForm.exchange_type} 
                        onValueChange={(v) => setExchangeForm({...exchangeForm, exchange_type: v})}
                      >
                        <SelectTrigger data-testid="exchange-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="gave">I gave time (earn credits)</SelectItem>
                          <SelectItem value="received">I received time (spend credits)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Contact Method Section */}
                  <div className="space-y-2">
                    <Label>How did you contact each other?</Label>
                    <Select 
                      value={exchangeForm.contact_method} 
                      onValueChange={(v) => setExchangeForm({...exchangeForm, contact_method: v})}
                    >
                      <SelectTrigger data-testid="exchange-contact-method">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CONTACT_METHODS.map(method => (
                          <SelectItem key={method.value} value={method.value}>
                            <span className="flex items-center gap-2">
                              <method.icon className="w-4 h-4" />
                              {method.label}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Contact Notes (optional)</Label>
                    <Input
                      placeholder="e.g., Met at local library, used WhatsApp video call"
                      value={exchangeForm.contact_notes}
                      onChange={(e) => setExchangeForm({...exchangeForm, contact_notes: e.target.value})}
                      data-testid="exchange-contact-notes"
                    />
                  </div>
                  
                  {/* Recurrence Section */}
                  <div className="space-y-2 p-3 rounded-md border-2 border-black bg-white">
                    <Label className="font-sans font-semibold">Recurring schedule (optional)</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <Select 
                        value={exchangeForm.recurrence} 
                        onValueChange={(v) => setExchangeForm({...exchangeForm, recurrence: v})}
                      >
                        <SelectTrigger data-testid="exchange-recurrence">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No, one-time</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="biweekly">Every 2 weeks</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        type="date"
                        value={exchangeForm.recurrence_end_date}
                        onChange={(e) => setExchangeForm({...exchangeForm, recurrence_end_date: e.target.value})}
                        disabled={exchangeForm.recurrence === 'none'}
                        placeholder="End date"
                        data-testid="exchange-recurrence-end"
                      />
                    </div>
                    {exchangeForm.recurrence !== 'none' && (
                      <p className="text-xs text-black/60 font-sans">
                        Future pending exchanges will be auto-created up to your end date (max 24 occurrences). You can edit or cancel each individually.
                      </p>
                    )}
                  </div>
                  
                  {/* Privacy Toggle */}
                  <div className="flex items-center justify-between p-3 bg-[#c7dfe9] rounded-lg">
                    <div className="flex items-center gap-2">
                      {exchangeForm.is_private ? (
                        <Lock className="w-4 h-4 text-[#d1c7e9]" />
                      ) : (
                        <Globe className="w-4 h-4 text-[#ffd703]" />
                      )}
                      <div>
                        <p className="text-sm font-medium text-[#090000]">
                          {exchangeForm.is_private ? 'Private Exchange' : 'Public Exchange'}
                        </p>
                        <p className="text-xs text-[#090000/60]">
                          {exchangeForm.is_private 
                            ? 'Only you, your partner, and admins can see this'
                            : 'Visible on the public community board'}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={!exchangeForm.is_private}
                      onCheckedChange={(checked) => setExchangeForm({...exchangeForm, is_private: !checked})}
                      data-testid="exchange-privacy-toggle"
                    />
                  </div>
                  
                  <div className="p-3 bg-[#c7dfe9] rounded-lg text-sm text-[#090000/60]">
                    <p>
                      {exchangeForm.exchange_type === 'gave' 
                        ? `You will earn ${exchangeForm.hours} credit(s) when this exchange is completed.`
                        : `You will spend ${exchangeForm.hours} credit(s) when this exchange is completed.`
                      }
                    </p>
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowNewExchange(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-[#ffd703] hover:bg-[#e6c203]" disabled={submitting} data-testid="exchange-submit">
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Log Exchange
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#ffd703]" />
          </div>
        ) : exchanges.length === 0 ? (
          <Card className="">
            <CardContent className="py-12 text-center">
              <ArrowRightLeft className="w-12 h-12 mx-auto text-[#d1c7e9] mb-4" />
              <h3 className="font-medium text-[#090000] mb-2">No exchanges yet</h3>
              <p className="text-sm text-[#090000/60] mb-4">
                Start by logging your first time exchange with a community member
              </p>
              <Button onClick={() => setShowNewExchange(true)} className="bg-[#ffd703] hover:bg-[#e6c203]">
                <Plus className="w-4 h-4 mr-2" />
                Log Exchange
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {/* Pending Exchanges */}
            {pendingExchanges.length > 0 && (
              <div>
                <h2 className="text-lg font-medium text-[#090000] mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#ffd703]" />
                  Pending Confirmation ({pendingExchanges.length})
                </h2>
                <div className="space-y-4">
                  {pendingExchanges.map((exchange) => (
                    <Card key={exchange.exchange_id} className="" data-testid={`exchange-${exchange.exchange_id}`}>
                      <CardContent className="p-6">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              {getStatusBadge(exchange.status)}
                              {exchange.is_private ? (
                                <Badge variant="outline" className="text-xs"><Lock className="w-3 h-3 mr-1" />Private</Badge>
                              ) : (
                                <Badge variant="outline" className="text-xs"><Globe className="w-3 h-3 mr-1" />Public</Badge>
                              )}
                              <span className="text-sm text-[#090000/60]">
                                {new Date(exchange.created_at).toLocaleDateString()}
                              </span>
                            </div>
                            <h3 className="font-medium text-[#090000] mb-1">{exchange.description}</h3>
                            <div className="flex flex-wrap items-center gap-4 text-sm text-[#090000/60]">
                              <span className="flex items-center gap-1">
                                <User className="w-4 h-4" />
                                {exchange.initiator_id === user?.user_id ? 'You' : exchange.initiator_name}
                                {' → '}
                                {exchange.partner_id === user?.user_id ? 'You' : exchange.partner_name}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {exchange.hours} hour(s)
                              </span>
                              <span className="flex items-center gap-1">
                                {React.createElement(getContactMethodIcon(exchange.contact_method), { className: "w-4 h-4" })}
                                {getContactMethodLabel(exchange.contact_method)}
                              </span>
                            </div>
                            {exchange.contact_notes && (
                              <p className="text-xs text-[#090000/60] mt-1 italic">Note: {exchange.contact_notes}</p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            {exchange.partner_id === user?.user_id && (
                              <Button 
                                onClick={() => handleConfirmExchange(exchange.exchange_id)}
                                className="bg-[#22c55e] hover:bg-[#5a7a1f]"
                                data-testid={`confirm-${exchange.exchange_id}`}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Confirm
                              </Button>
                            )}
                            <Button 
                              variant="outline" 
                              onClick={() => handleCancelExchange(exchange.exchange_id)}
                              className="border-[#ef4444] text-[#ef4444] hover:bg-[#ef4444]/10"
                              data-testid={`cancel-${exchange.exchange_id}`}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Cancel
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Confirmed Exchanges */}
            {confirmedExchanges.length > 0 && (
              <div>
                <h2 className="text-lg font-medium text-[#090000] mb-4 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#d1c7e9]" />
                  Ready to Complete ({confirmedExchanges.length})
                </h2>
                <div className="space-y-4">
                  {confirmedExchanges.map((exchange) => (
                    <Card key={exchange.exchange_id} className="" data-testid={`exchange-${exchange.exchange_id}`}>
                      <CardContent className="p-6">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              {getStatusBadge(exchange.status)}
                              {exchange.is_private ? (
                                <Badge variant="outline" className="text-xs"><Lock className="w-3 h-3 mr-1" />Private</Badge>
                              ) : (
                                <Badge variant="outline" className="text-xs"><Globe className="w-3 h-3 mr-1" />Public</Badge>
                              )}
                              <span className="text-sm text-[#090000/60]">
                                {new Date(exchange.created_at).toLocaleDateString()}
                              </span>
                            </div>
                            <h3 className="font-medium text-[#090000] mb-1">{exchange.description}</h3>
                            <div className="flex flex-wrap items-center gap-4 text-sm text-[#090000/60]">
                              <span className="flex items-center gap-1">
                                <User className="w-4 h-4" />
                                {exchange.initiator_id === user?.user_id ? 'You' : exchange.initiator_name}
                                {' → '}
                                {exchange.partner_id === user?.user_id ? 'You' : exchange.partner_name}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {exchange.hours} hour(s)
                              </span>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              onClick={() => handleCompleteExchange(exchange.exchange_id)}
                              className="bg-[#22c55e] hover:bg-[#5a7a1f]"
                              data-testid={`complete-${exchange.exchange_id}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Complete & Update Credits
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Completed Exchanges */}
            {completedExchanges.length > 0 && (
              <div>
                <h2 className="text-lg font-medium text-[#090000] mb-4 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#22c55e]" />
                  Completed ({completedExchanges.length})
                </h2>
                <div className="space-y-4">
                  {completedExchanges.map((exchange) => (
                    <Card key={exchange.exchange_id} className="" data-testid={`exchange-${exchange.exchange_id}`}>
                      <CardContent className="p-6">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              {getStatusBadge(exchange.status)}
                              {exchange.is_private ? (
                                <Badge variant="outline" className="text-xs"><Lock className="w-3 h-3 mr-1" />Private</Badge>
                              ) : (
                                <Badge variant="outline" className="text-xs"><Globe className="w-3 h-3 mr-1" />Public</Badge>
                              )}
                              <span className="text-sm text-[#090000/60]">
                                Completed {exchange.completed_at ? new Date(exchange.completed_at).toLocaleDateString() : ''}
                              </span>
                            </div>
                            <h3 className="font-medium text-[#090000] mb-1">{exchange.description}</h3>
                            <div className="flex flex-wrap items-center gap-4 text-sm text-[#090000/60]">
                              <span className="flex items-center gap-1">
                                <User className="w-4 h-4" />
                                {exchange.initiator_id === user?.user_id ? 'You' : exchange.initiator_name}
                                {' → '}
                                {exchange.partner_id === user?.user_id ? 'You' : exchange.partner_name}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {exchange.hours} hour(s)
                              </span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
